import React, { useEffect, useState } from 'react';
import { settingsApi } from '../services/api';
import {
  GOOGLE_MODELS,
  OPENAI_MODELS,
  ANTHROPIC_MODELS,
  DEFAULT_PROVIDER,
  DEFAULT_MODEL,
} from '../types';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Toggle } from './ui/toggle';
import { Select } from './ui/Select';

interface AdminSettingsProps {
  onClose?: () => void;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ onClose }) => {
  const [isQueueEnabled, setIsQueueEnabled] = useState(false);
  const [isTogglingQueue, setIsTogglingQueue] = useState(false);
  const [maxConcurrentChunks, setMaxConcurrentChunks] = useState(5);
  const [maxQueueSize, setMaxQueueSize] = useState(100);
  const [defaultAIProvider, setDefaultAIProvider] = useState<string>(DEFAULT_PROVIDER);
  const [defaultAIModel, setDefaultAIModel] = useState<string>(DEFAULT_MODEL);
  const [maxUrlsNonAdmin, setMaxUrlsNonAdmin] = useState(100);
  const [clickhouseMaxConnections, setClickhouseMaxConnections] = useState(50);
  const [clickhouseMinConnections, setClickhouseMinConnections] = useState(5);
  const [clickhouseIdleTimeout, setClickhouseIdleTimeout] = useState(60000);
  const [clickhouseAcquireTimeout, setClickhouseAcquireTimeout] = useState(10000);
  const [scheduledQueueMaxWorkers, setScheduledQueueMaxWorkers] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // GCP Service Account state
  const [gcpServiceAccountUploaded, setGcpServiceAccountUploaded] = useState(false);
  const [gcpProjectId, setGcpProjectId] = useState<string | null>(null);
  const [gcpServiceAccountEmail, setGcpServiceAccountEmail] = useState<string | null>(null);
  const [uploadingServiceAccount, setUploadingServiceAccount] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchGCPServiceAccountStatus();
  }, []);

  const fetchGCPServiceAccountStatus = async () => {
    try {
      const response = await settingsApi.getGCPServiceAccountStatus();
      setGcpServiceAccountUploaded(response.uploaded);
      setGcpProjectId(response.projectId || null);
      setGcpServiceAccountEmail(response.serviceAccountEmail || null);
    } catch (err) {
      console.error('Failed to fetch GCP service account status:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settings = await settingsApi.getAll();
      
      // Find the specific settings
      const queueSetting = settings.find(s => s.key_name === 'analysis_queue_enabled');
      const maxChunksSetting = settings.find(s => s.key_name === 'max_concurrent_chunks');
      const maxQueueSizeSetting = settings.find(s => s.key_name === 'max_queue_size');
      const providerSetting = settings.find(s => s.key_name === 'default_ai_provider');
      const modelSetting = settings.find(s => s.key_name === 'default_ai_model');
      const maxUrlsNonAdminSetting = settings.find(s => s.key_name === 'max_urls_non_admin');
      const clickhouseMaxSetting = settings.find(s => s.key_name === 'clickhouse_max_connections');
      const clickhouseMinSetting = settings.find(s => s.key_name === 'clickhouse_min_connections');
      const clickhouseIdleSetting = settings.find(s => s.key_name === 'clickhouse_idle_timeout_ms');
      const clickhouseAcquireSetting = settings.find(s => s.key_name === 'clickhouse_acquire_timeout_ms');
      const scheduledQueueWorkersSetting = settings.find(s => s.key_name === 'scheduled_queue_max_workers');
      
      setIsQueueEnabled(queueSetting?.value === 'true' || queueSetting?.value === true);
      setMaxConcurrentChunks(Number(maxChunksSetting?.value) || 5);
      setMaxQueueSize(Number(maxQueueSizeSetting?.value) || 100);

      // Set provider and validate model belongs to that provider
      const provider = providerSetting?.value || DEFAULT_PROVIDER;
      const model = modelSetting?.value || DEFAULT_MODEL;
      setDefaultAIProvider(provider);

      // Get valid models for the provider
      const validModels: string[] = provider === 'google' ? [...GOOGLE_MODELS]
        : provider === 'openai' ? [...OPENAI_MODELS]
        : [...ANTHROPIC_MODELS];

      // If the model is valid for the provider, use it; otherwise use the first valid model
      if (validModels.includes(model)) {
        setDefaultAIModel(model);
      } else {
        console.warn(`Model ${model} is not valid for provider ${provider}, using ${validModels[0]}`);
        setDefaultAIModel(validModels[0]);
      }

      setMaxUrlsNonAdmin(Number(maxUrlsNonAdminSetting?.value) || 100);
      setClickhouseMaxConnections(Number(clickhouseMaxSetting?.value) || 50);
      setClickhouseMinConnections(Number(clickhouseMinSetting?.value) || 5);
      setClickhouseIdleTimeout(Number(clickhouseIdleSetting?.value) || 60000);
      setClickhouseAcquireTimeout(Number(clickhouseAcquireSetting?.value) || 10000);
      setScheduledQueueMaxWorkers(Number(scheduledQueueWorkersSetting?.value) || 100);
    } catch (err: any) {
      console.error('Failed to fetch settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Available AI providers and their models (using shared constants)
  const aiProviders = {
    openai: {
      name: 'OpenAI',
      models: [...OPENAI_MODELS]
    },
    google: {
      name: 'Google',
      models: [...GOOGLE_MODELS]
    },
    anthropic: {
      name: 'Anthropic',
      models: [...ANTHROPIC_MODELS]
    }
  };

  const handleUpdateAISettings = async () => {
    try {
      // Update both provider and model together
      await settingsApi.update('default_ai_provider', defaultAIProvider);
      await settingsApi.update('default_ai_model', defaultAIModel);
      setSuccessMessage('AI settings updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to update AI settings:', err);
      setError(err.response?.data?.error || 'Failed to update AI settings');
    }
  };

  const handleProviderChange = (newProvider: string) => {
    setDefaultAIProvider(newProvider);
    // Auto-select first model of the new provider
    const providerModels = aiProviders[newProvider as keyof typeof aiProviders]?.models;
    if (providerModels && providerModels.length > 0) {
      setDefaultAIModel(providerModels[0]);
    }
  };

  const handleToggleQueue = async () => {
    try {
      setIsTogglingQueue(true);
      const newValue = !isQueueEnabled;
      
      await settingsApi.update('analysis_queue_enabled', newValue);
      setIsQueueEnabled(newValue);
      
      // Show success message
      setError(null);
    } catch (err: any) {
      console.error('Failed to toggle analysis queue:', err);
      setError(err.response?.data?.error || 'Failed to toggle analysis queue');
    } finally {
      setIsTogglingQueue(false);
    }
  };

  const handleUpdateCapacitySetting = async (key: 'max_concurrent_chunks' | 'max_queue_size' | 'max_urls_non_admin' | 'clickhouse_max_connections' | 'clickhouse_min_connections' | 'clickhouse_idle_timeout_ms' | 'clickhouse_acquire_timeout_ms' | 'scheduled_queue_max_workers', value: number) => {
    try {
      await settingsApi.update(key, value);
      setSuccessMessage('Setting updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error(`Failed to update ${key}:`, err);
      setError(err.response?.data?.error || `Failed to update ${key}`);
    }
  };

  const handleUploadServiceAccount = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    try {
      setUploadingServiceAccount(true);
      setError(null);

      const formData = new FormData();
      formData.append('serviceAccount', file);

      const response = await settingsApi.uploadGCPServiceAccount(formData);

      setGcpServiceAccountUploaded(true);
      setGcpProjectId(response.projectId);
      setGcpServiceAccountEmail(response.serviceAccountEmail);
      setSuccessMessage('GCP service account uploaded successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to upload service account:', err);
      setError(err.response?.data?.error || 'Failed to upload service account file');
    } finally {
      setUploadingServiceAccount(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading settings...</div>
      </div>
    );
  }

  const pillInput = "rounded-full border border-line bg-surface px-3.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent";

  return (
    <Card>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-ink">Admin Settings</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink-muted"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-risk-bg border border-risk rounded-md p-4">
            <p className="text-risk text-sm">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 bg-safe-bg border border-safe rounded-md p-4">
            <p className="text-safe text-sm">{successMessage}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Default AI Provider and Model */}
          <div className="bg-canvas rounded-xl p-4">
            <h3 className="text-sm font-bold mb-4 text-ink">Default AI Settings</h3>

            {/* AI Provider and Model */}
            <div className="space-y-4">
              <p className="text-xs text-ink-muted">
                Default provider and model for all new analyses (non-admin users cannot change this)
              </p>

              <div className="flex items-end space-x-4">
                {/* Provider */}
                <div className="w-40">
                  <label className="block text-sm font-medium text-ink mb-1">Provider</label>
                  <Select
                    value={defaultAIProvider}
                    onChange={handleProviderChange}
                    options={Object.entries(aiProviders).map(([key, provider]) => ({ value: key, label: provider.name }))}
                    data-testid="admin-provider-select"
                  />
                </div>

                {/* Model */}
                <div className="w-56">
                  <label className="block text-sm font-medium text-ink mb-1">Model</label>
                  <Select
                    value={defaultAIModel}
                    onChange={setDefaultAIModel}
                    options={(aiProviders[defaultAIProvider as keyof typeof aiProviders]?.models || []).map((model) => ({ value: model, label: model }))}
                    data-testid="admin-model-select"
                  />
                </div>

                <Button onClick={handleUpdateAISettings}>Update</Button>
              </div>
            </div>
          </div>

          {/* Max URLs for Non-Admin Users */}
          <div className="bg-canvas rounded-xl p-4">
            <label className="block">
              <span className="text-sm font-medium text-ink">Maximum URLs for Non-Admin Users</span>
              <p className="text-xs text-ink-muted mt-1">
                Maximum number of URLs that non-admin users can analyze in a single request
              </p>
              <div className="mt-2 flex items-center space-x-4">
                <input
                  type="number"
                  value={maxUrlsNonAdmin}
                  onChange={(e) => setMaxUrlsNonAdmin(Number(e.target.value))}
                  min={1}
                  max={1000}
                  className={`${pillInput} w-24`}
                />
                <Button size="sm" onClick={() => handleUpdateCapacitySetting('max_urls_non_admin', maxUrlsNonAdmin)}>Update</Button>
              </div>
            </label>
          </div>

          {/* Analysis Queue Toggle */}
          <div className="bg-canvas rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-ink">Analysis Queue System</h3>
                <p className="text-xs text-ink-muted mt-1">
                  Enable queue-based processing for analysis jobs with capacity management
                </p>
              </div>
              <Toggle checked={isQueueEnabled} onChange={handleToggleQueue} disabled={isTogglingQueue} />
            </div>
          </div>

          {/* Queue Capacity Settings - Only show if queue is enabled */}
          {isQueueEnabled && (
            <div className="border-t border-line pt-4 space-y-4">
              <h3 className="text-sm font-bold text-ink">Queue Capacity Settings</h3>

              {/* Max Concurrent Chunks */}
              <div className="bg-canvas rounded-xl p-4">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Maximum Concurrent Chunks</span>
                  <p className="text-xs text-ink-muted mt-1">
                    Maximum number of analysis chunks that can be processed simultaneously
                  </p>
                  <div className="mt-2 flex items-center space-x-4">
                    <input
                      type="number"
                      value={maxConcurrentChunks}
                      onChange={(e) => setMaxConcurrentChunks(Number(e.target.value))}
                      min={1}
                      max={100}
                      className={`${pillInput} w-24`}
                    />
                    <Button size="sm" onClick={() => handleUpdateCapacitySetting('max_concurrent_chunks', maxConcurrentChunks)}>Update</Button>
                  </div>
                </label>
              </div>

              {/* Max Queue Size */}
              <div className="bg-canvas rounded-xl p-4">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Maximum Queue Size</span>
                  <p className="text-xs text-ink-muted mt-1">
                    Maximum number of jobs that can be queued
                  </p>
                  <div className="mt-2 flex items-center space-x-4">
                    <input
                      type="number"
                      value={maxQueueSize}
                      onChange={(e) => setMaxQueueSize(Number(e.target.value))}
                      min={10}
                      max={1000}
                      className={`${pillInput} w-24`}
                    />
                    <Button size="sm" onClick={() => handleUpdateCapacitySetting('max_queue_size', maxQueueSize)}>Update</Button>
                  </div>
                </label>
              </div>

              {/* Scheduled Queue Max Workers */}
              <div className="bg-canvas rounded-xl p-4">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Scheduled Queue Max Workers</span>
                  <p className="text-xs text-ink-muted mt-1">
                    Maximum number of concurrent workers for scheduled job processing (recommended: 100 for bulk processing)
                  </p>
                  <div className="mt-2 flex items-center space-x-4">
                    <input
                      type="number"
                      value={scheduledQueueMaxWorkers}
                      onChange={(e) => setScheduledQueueMaxWorkers(Number(e.target.value))}
                      min={10}
                      max={200}
                      className={`${pillInput} w-24`}
                    />
                    <Button size="sm" onClick={() => handleUpdateCapacitySetting('scheduled_queue_max_workers', scheduledQueueMaxWorkers)}>Update</Button>
                    <span className="text-xs text-ink-muted">Current: {scheduledQueueMaxWorkers} workers</span>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* ClickHouse Connection Pool Settings */}
          <div className="border-t border-line pt-4 space-y-4">
            <h3 className="text-sm font-bold text-ink">ClickHouse Connection Pool Settings</h3>

            {/* Max Connections */}
            <div className="bg-canvas rounded-xl p-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Maximum Connections</span>
                <p className="text-xs text-ink-muted mt-1">
                  Maximum number of connections in the ClickHouse pool (should match max concurrent chunks)
                </p>
                <div className="mt-2 flex items-center space-x-4">
                  <input
                    type="number"
                    value={clickhouseMaxConnections}
                    onChange={(e) => setClickhouseMaxConnections(Number(e.target.value))}
                    min={5}
                    max={100}
                    className={`${pillInput} w-24`}
                  />
                  <Button size="sm" onClick={() => handleUpdateCapacitySetting('clickhouse_max_connections', clickhouseMaxConnections)}>Update</Button>
                  <span className="text-xs text-ink-muted">Recommended: 50 for ClickHouse Cloud</span>
                </div>
              </label>
            </div>

            {/* Min Connections */}
            <div className="bg-canvas rounded-xl p-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Minimum Connections</span>
                <p className="text-xs text-ink-muted mt-1">
                  Minimum number of connections to keep ready in the pool
                </p>
                <div className="mt-2 flex items-center space-x-4">
                  <input
                    type="number"
                    value={clickhouseMinConnections}
                    onChange={(e) => setClickhouseMinConnections(Number(e.target.value))}
                    min={1}
                    max={20}
                    className={`${pillInput} w-24`}
                  />
                  <Button size="sm" onClick={() => handleUpdateCapacitySetting('clickhouse_min_connections', clickhouseMinConnections)}>Update</Button>
                </div>
              </label>
            </div>

            {/* Idle Timeout */}
            <div className="bg-canvas rounded-xl p-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Idle Timeout (ms)</span>
                <p className="text-xs text-ink-muted mt-1">
                  Time before idle connections are removed from the pool
                </p>
                <div className="mt-2 flex items-center space-x-4">
                  <input
                    type="number"
                    value={clickhouseIdleTimeout}
                    onChange={(e) => setClickhouseIdleTimeout(Number(e.target.value))}
                    min={10000}
                    max={300000}
                    step={10000}
                    className={`${pillInput} w-32`}
                  />
                  <Button size="sm" onClick={() => handleUpdateCapacitySetting('clickhouse_idle_timeout_ms', clickhouseIdleTimeout)}>Update</Button>
                  <span className="text-xs text-ink-muted">{clickhouseIdleTimeout / 1000}s</span>
                </div>
              </label>
            </div>

            {/* Acquire Timeout */}
            <div className="bg-canvas rounded-xl p-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Acquire Timeout (ms)</span>
                <p className="text-xs text-ink-muted mt-1">
                  Maximum time to wait for an available connection
                </p>
                <div className="mt-2 flex items-center space-x-4">
                  <input
                    type="number"
                    value={clickhouseAcquireTimeout}
                    onChange={(e) => setClickhouseAcquireTimeout(Number(e.target.value))}
                    min={1000}
                    max={30000}
                    step={1000}
                    className={`${pillInput} w-32`}
                  />
                  <Button size="sm" onClick={() => handleUpdateCapacitySetting('clickhouse_acquire_timeout_ms', clickhouseAcquireTimeout)}>Update</Button>
                  <span className="text-xs text-ink-muted">{clickhouseAcquireTimeout / 1000}s</span>
                </div>
              </label>
            </div>
          </div>

          {/* GCP Service Account Upload */}
          <div className="border-t border-line pt-4">
            <h3 className="text-sm font-bold mb-4 text-ink">Google Cloud Platform Service Account</h3>

            <div className="bg-canvas rounded-xl p-4">
              <div className="mb-4">
                <span className="text-sm font-medium text-ink">Service Account Credentials</span>
                <p className="text-xs text-ink-muted mt-1">
                  Upload your GCP service account JSON file for Equativ sync to Google Cloud Storage
                </p>
              </div>

              {gcpServiceAccountUploaded ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-safe" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-safe font-medium">Service account configured</span>
                  </div>

                  {gcpProjectId && (
                    <div className="text-xs text-ink-muted">
                      <strong>Project ID:</strong> {gcpProjectId}
                    </div>
                  )}

                  {gcpServiceAccountEmail && (
                    <div className="text-xs text-ink-muted">
                      <strong>Service Account:</strong> {gcpServiceAccountEmail}
                    </div>
                  )}

                  <div className="pt-2">
                    <label className="cursor-pointer inline-flex items-center justify-center rounded-full bg-gradient-accent text-white text-sm font-semibold px-4 py-2 hover:opacity-90">
                      {uploadingServiceAccount ? 'Uploading...' : 'Replace Service Account'}
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleUploadServiceAccount}
                        disabled={uploadingServiceAccount}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="cursor-pointer inline-flex items-center justify-center rounded-full bg-gradient-accent text-white text-sm font-semibold px-4 py-2 hover:opacity-90">
                    {uploadingServiceAccount ? 'Uploading...' : 'Upload Service Account JSON'}
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleUploadServiceAccount}
                      disabled={uploadingServiceAccount}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-ink-muted mt-2">
                    No service account configured. Equativ sync will not work without this.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-warn-bg rounded-xl p-4">
            <h4 className="text-sm font-bold text-warn mb-2">Important</h4>
            <ul className="text-xs text-warn space-y-1">
              <li>• Default AI settings apply to all non-admin users</li>
              <li>• Admin users can override these defaults when creating analyses</li>
              <li>• Analysis Queue helps manage system load and prevents overload</li>
              <li>• ClickHouse connection pool settings affect database performance</li>
              <li>• GCP service account is required for Equativ sync functionality</li>
              <li>• Changes take effect immediately without restart</li>
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AdminSettings;